import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";
import CalendarEventsFilterInfo from "../../context/calendarEventsFilterInfo";
import { CalendarEvent } from "../../models/CalendarEvent";
import { Dropoff } from "../../models/Dropoff";
import PlotAPI from "../../plotAPI";
import TeamInfo from "../../teamInfo";

dayjs.extend(duration);
dayjs.extend(isBetween);

/**
 * Formats a given delivery event into a "cleaned" version
 *
 **/

export const convertSecondsToHumanReadable = (seconds: number) => {
    let timeFormatted = "";
    const duration = dayjs.duration(seconds, "seconds");
    if (duration.hours() > 0) {
        timeFormatted = duration.format("H[h] m[m]");
    } else {
        timeFormatted = duration.format("m[mins]");
    }

    return timeFormatted;
};

const getNextYearsClosures = (project: any): Promise<CalendarEvent[]> => {
    const startOfSelectedYear = dayjs().tz(project.timezone).set("year", dayjs().year()).startOf("year");
    const endOfSelectedYear = startOfSelectedYear.add(1, "year");

    const calendarEventsFilterInfo = CalendarEventsFilterInfo.fromApiObject(
        {
            project_ids: [project.id],
            calendar_event_types: ["unavailability"],
            calendarable_types: [-1],
            calendar_event_resources: [
                {
                    calendar_resourceable_id: project.id,
                    calendar_resourceable_type: "Project",
                },
            ],
            start_at: startOfSelectedYear.toString(),
            end_at: endOfSelectedYear.toString(),
            include_background_events: false,
            archived: false,
        },
        project.timezone
    );

    return PlotAPI.getCalendarEvents(
        project.claimed_by_team_subdomain || "projects",
        project.unique_token,
        calendarEventsFilterInfo,
        project.timezone
    ).then((response) => {
        return response.calendar_events;
    });
};

// Get upcoming closure days for a given project and return the day interger values
export const upcomingClosureDaysForProject = async (
    project: any
): Promise<{
    closedDaysIndex: Set<number>;
    nextClosureDay: { name: string; date: string } | undefined;
}> => {
    if (!project?.unique_token) {
        return { closedDaysIndex: new Set<number>(), nextClosureDay: undefined };
    }

    const jobsiteClosureDays = await getNextYearsClosures(project);

    const closedDaysIndex = new Set<number>();

    jobsiteClosureDays
        .filter((event: CalendarEvent) => {
            return (
                dayjs(event.startAt).isBetween(dayjs().startOf("week"), dayjs().endOf("week"), "day", "[]") ||
                dayjs(event.endAt).isBetween(dayjs().startOf("week"), dayjs().endOf("week"), "day", "[]")
            );
        })
        .forEach((event: CalendarEvent) => {
            const exceptionDayIndex = dayjs(event.startAt).day();

            // Endings for a full day are the start of the next day, so jump one back to get the correct day index.
            const exceptionDayEndIndex = dayjs(event.endAt).subtract(1, "day").day();
            if (exceptionDayIndex !== exceptionDayEndIndex) {
                closedDaysIndex.add(exceptionDayIndex);
                closedDaysIndex.add(exceptionDayEndIndex);
            } else {
                closedDaysIndex.add(exceptionDayIndex);
            }
        });

    // Find the next upcoming closure (either exception day or holiday) after today
    const nextException = jobsiteClosureDays
        ?.filter((day: CalendarEvent) => dayjs(day.startAt).isAfter(dayjs(), "day"))
        .sort((a: CalendarEvent, b: CalendarEvent) => dayjs(a.startAt).diff(dayjs(b.startAt)))[0];

    let nextClosure: { name: string; date: string } | undefined = undefined;

    if (nextException) {
        nextClosure = {
            name: nextException.name,
            date: nextException.startAt.format("MMMM D, YYYY"),
        };
    }

    return {
        closedDaysIndex: closedDaysIndex,
        nextClosureDay: nextClosure,
    };
};

export interface TeamDeliveries {
    name: string;
    teamId: number;
    count: number;
    value: number;
    teamInfo: TeamInfo;
    deliveries: CalendarEvent[];
}

export interface DropoffDeliveries {
    name: string;
    dropoffId: number;
    count: number;
    value: number;
    dropoff: Dropoff;
    deliveries: CalendarEvent[];
}
